/**
 * Animação de estímulos.
 *
 * `frame` é uma função PURA do tempo normalizado, o que traz duas consequências
 * boas: `frame(0.5)` é testável por snapshot sem timing de DOM, e o quadro estático
 * de fallback é só `frame(staticFallbackAt)` — a mesma fonte, sem código paralelo.
 *
 * Três comportamentos que NÃO são opcionais:
 *
 *  - `prefers-reduced-motion: reduce` não dá autoplay; renderiza o quadro estático.
 *    Movimento tem custo real de acessibilidade.
 *  - Sempre há play/pause/replay e barra de posição. Uma animação que o aluno não
 *    pode repetir é falha de acessibilidade E confusão de medida: não se distingue
 *    "não sabia" de "não viu".
 *  - O tempo de exibição contamina o tempo de resposta. Por isso o player registra
 *    os eventos de reprodução separadamente: dá para descontar leitura de raciocínio.
 */

import type { Scene } from './scene.ts';
import { renderScene } from './svg.ts';

export interface Animator {
  play(): void;
  pause(): void;
  seek(t: number): void;
  toggle(): void;
  dispose(): void;
  readonly t: number;
  readonly playing: boolean;
}

export interface AnimateOptions {
  readonly durationMs: number;
  readonly loop: boolean;
  readonly autoplay?: boolean;
  readonly onTick?: (t: number) => void;
  readonly onPlayState?: (playing: boolean) => void;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Anima dentro de `host`, substituindo o conteúdo a cada quadro.
 *
 * Redesenhar a cena inteira (em vez de aplicar um patch por atributo) é simples e
 * suficiente para figuras desta ordem de grandeza. Como a `Scene` é dado, trocar isto
 * por um patch com chave depois é uma otimização local — não uma reescrita.
 */
export function animate(
  host: HTMLElement,
  frame: (t: number) => Scene,
  opts: AnimateOptions,
): Animator {
  let t = 0;
  let playing = false;
  let raf = 0;
  let ultimo = 0;

  const draw = (): void => {
    host.replaceChildren(renderScene(frame(t)));
    opts.onTick?.(t);
  };

  const step = (agora: number): void => {
    if (!playing) return;
    const dt = ultimo === 0 ? 0 : agora - ultimo;
    ultimo = agora;
    t += dt / opts.durationMs;

    if (t >= 1) {
      if (opts.loop) t %= 1;
      else {
        t = 1;
        setPlaying(false);
      }
    }
    draw();
    if (playing) raf = requestAnimationFrame(step);
  };

  const setPlaying = (v: boolean): void => {
    if (playing === v) return;
    playing = v;
    opts.onPlayState?.(v);
  };

  const api: Animator = {
    play() {
      if (playing) return;
      if (t >= 1) t = 0;
      setPlaying(true);
      ultimo = 0;
      raf = requestAnimationFrame(step);
    },
    pause() {
      setPlaying(false);
      cancelAnimationFrame(raf);
    },
    toggle() {
      if (playing) api.pause();
      else api.play();
    },
    seek(v: number) {
      t = Math.max(0, Math.min(1, v));
      draw();
    },
    dispose() {
      setPlaying(false);
      cancelAnimationFrame(raf);
    },
    get t() {
      return t;
    },
    get playing() {
      return playing;
    },
  };

  draw();
  if (opts.autoplay !== false && !prefersReducedMotion()) api.play();
  return api;
}
